import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Plus, FileText, Download, Upload, Pencil, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AdrnsIntelligence from './AdrnsIntelligence';
import AdrnsDossierMissions from './AdrnsDossierMissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORG_TYPES = { porteur: 'Porteur de projet', entreprise: 'Entreprise', association: 'Association', dirigeant: 'Dirigeant' };
const STATUTS = ['actif', 'pause', 'termine'];
const STATUT_STYLES = { actif: 'bg-emerald-50 text-emerald-700 border-emerald-200', pause: 'bg-amber-50 text-amber-700 border-amber-200', termine: 'bg-stone-100 text-stone-600 border-stone-200' };
const INTERV_TYPES = { session: 'Session', appel: 'Appel', atelier: 'Atelier', note: 'Note' };

export default function AdrnsDossierDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const [data, setData] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [intervForm, setIntervForm] = useState({ type: 'session', module_id: '', notes: '', duree_min: '' });
    const [savingInterv, setSavingInterv] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    const reload = () => axios.get(`${API}/adrns/dossiers/${id}`, hdrs())
        .then(r => setData(r.data))
        .catch(e => { toast.error(e.response?.data?.detail || e.message); navigate('/app/adrns/dossiers'); });
    useEffect(() => { if (session) reload(); /* eslint-disable-next-line */ }, [session, id]);

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;
    const { dossier: d, interventions, documents, members, modules, role } = data;
    const canAssign = role === 'coordinateur';

    const patch = async (updates, msg = 'Dossier mis à jour') => {
        try {
            await axios.patch(`${API}/adrns/dossiers/${id}`, updates, hdrs());
            toast.success(msg);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const toggleModule = (mid) => patch({ modules_actifs: { ...d.modules_actifs, [String(mid)]: !d.modules_actifs?.[String(mid)] } }, 'Modules mis à jour');

    const toggleMember = (uid) => {
        const cur = d.assigned_member_ids || [];
        const next = cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid];
        patch({ assigned_member_ids: next }, 'Assignation mise à jour');
    };

    const saveEdit = async () => { await patch(editForm); setEditOpen(false); };

    const addInterv = async () => {
        setSavingInterv(true);
        try {
            await axios.post(`${API}/adrns/dossiers/${id}/interventions`, {
                type: intervForm.type,
                module_id: intervForm.module_id ? Number(intervForm.module_id) : null,
                notes: intervForm.notes,
                duree_min: intervForm.duree_min ? Number(intervForm.duree_min) : null,
            }, hdrs());
            toast.success('Intervention enregistrée');
            setIntervForm({ type: 'session', module_id: '', notes: '', duree_min: '' });
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSavingInterv(false);
    };

    const uploadDoc = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(`${API}/adrns/dossiers/${id}/documents`, fd, hdrs());
            toast.success(r.data.deduplicated ? 'Document déjà présent (dédupliqué)' : 'Document ajouté');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setUploading(false);
    };

    const downloadDoc = async (doc) => {
        try {
            const r = await axios.get(`${API}/adrns/documents/${doc.id}/download`, { ...hdrs(), responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = doc.name; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Téléchargement impossible'); }
    };

    return (
        <div className="space-y-6" data-testid="adrns-dossier-detail">
            <button onClick={() => navigate('/app/adrns/dossiers')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="adrns-detail-back">
                <ArrowLeft size={14} /> Tous les dossiers
            </button>

            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-1">{ORG_TYPES[d.org_type] || d.org_type}</div>
                    <h1 className="font-heading text-4xl" data-testid="adrns-detail-name">{d.porteur_name}</h1>
                    <div className="text-sm text-muted-foreground mt-1">
                        {d.porteur_email && <span>{d.porteur_email}</span>}{d.porteur_email && d.porteur_phone && ' · '}{d.porteur_phone}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <select value={d.statut || 'actif'} onChange={e => patch({ statut: e.target.value }, 'Statut mis à jour')}
                        className={`h-9 px-3 rounded-full border text-sm font-medium ${STATUT_STYLES[d.statut] || STATUT_STYLES.actif}`}
                        data-testid="adrns-detail-statut">
                        {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button variant="outline" size="sm" onClick={() => { setEditForm({ porteur_name: d.porteur_name, porteur_email: d.porteur_email || '', porteur_phone: d.porteur_phone || '', org_type: d.org_type, besoin: d.besoin || '' }); setEditOpen(true); }} data-testid="adrns-detail-edit-btn">
                        <Pencil size={13} className="mr-1.5" /> Modifier
                    </Button>
                </div>
            </div>

            {d.besoin && <div className="card-soft p-5 text-sm text-foreground/80"><span className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">Besoin exprimé</span>{d.besoin}</div>}

            <div className="card-soft p-6">
                <h2 className="font-heading text-xl mb-4">Modules d'accompagnement</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {modules.map(m => (
                        <div key={m.id} className={`border rounded-lg p-4 transition-colors ${d.modules_actifs?.[String(m.id)] ? 'border-emerald-300 bg-emerald-50/50' : 'border-border'}`} data-testid={`adrns-detail-module-${m.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Module {m.id}</div>
                                    <div className="font-medium text-sm mt-0.5">{m.nom}</div>
                                </div>
                                <Switch checked={!!d.modules_actifs?.[String(m.id)]} onCheckedChange={() => toggleModule(m.id)} data-testid={`adrns-module-toggle-${m.id}`} />
                            </div>
                            {d.modules_actifs?.[String(m.id)] && (
                                <button onClick={() => navigate(`/app/adrns/dossiers/${id}/module/${m.id}`)}
                                    className="mt-3 flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    data-testid={`adrns-module-open-${m.id}`}>
                                    Ouvrir les livrables <ArrowRight size={12} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="card-soft p-6">
                <h2 className="font-heading text-xl mb-4">Membres assignés</h2>
                <div className="flex flex-wrap gap-2">
                    {members.map(m => {
                        const assigned = (d.assigned_member_ids || []).includes(m.user_id);
                        return (
                            <button key={m.user_id} onClick={() => canAssign && toggleMember(m.user_id)} disabled={!canAssign}
                                className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${assigned ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground/60'} ${canAssign ? 'cursor-pointer' : 'cursor-default'}`}
                                data-testid={`adrns-assign-${m.user_id}`}>
                                {m.full_name || m.email}
                            </button>
                        );
                    })}
                    {members.length === 0 && <p className="text-sm text-foreground/60">Aucun membre ADRNS habilité pour le moment.</p>}
                </div>
                {!canAssign && <p className="text-xs text-muted-foreground mt-2">Seul un coordinateur peut modifier les assignations.</p>}
            </div>

            <AdrnsDossierMissions dossierId={id} />

            <AdrnsIntelligence dossierId={id} />

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="card-soft p-6">
                    <h2 className="font-heading text-xl mb-4">Interventions ({interventions.length})</h2>
                    <div className="space-y-2 mb-4">
                        <div className="flex gap-2">
                            <select value={intervForm.type} onChange={e => setIntervForm(f => ({ ...f, type: e.target.value }))}
                                className="h-9 px-2 rounded-md border border-input bg-background text-sm flex-1" data-testid="adrns-interv-type">
                                {Object.entries(INTERV_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <select value={intervForm.module_id} onChange={e => setIntervForm(f => ({ ...f, module_id: e.target.value }))}
                                className="h-9 px-2 rounded-md border border-input bg-background text-sm flex-1">
                                <option value="">Module (optionnel)</option>
                                {modules.map(m => <option key={m.id} value={m.id}>M{m.id} · {m.nom}</option>)}
                            </select>
                            <Input type="number" placeholder="min" className="w-20 h-9" value={intervForm.duree_min}
                                onChange={e => setIntervForm(f => ({ ...f, duree_min: e.target.value }))} />
                        </div>
                        <Textarea placeholder="Notes de l'intervention *" rows={2} value={intervForm.notes}
                            onChange={e => setIntervForm(f => ({ ...f, notes: e.target.value }))} data-testid="adrns-interv-notes" />
                        <Button size="sm" onClick={addInterv} disabled={savingInterv || intervForm.notes.trim().length < 2} data-testid="adrns-interv-submit">
                            {savingInterv ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} className="mr-1" /> Enregistrer</>}
                        </Button>
                    </div>
                    <ul className="space-y-3 max-h-80 overflow-y-auto">
                        {interventions.map(i => (
                            <li key={i.id} className="border-l-2 border-primary/40 pl-3 text-sm">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="uppercase tracking-wider font-medium text-primary">{INTERV_TYPES[i.type] || i.type}</span>
                                    {i.module_id && <span>· Module {i.module_id}</span>}
                                    {i.duree_min && <span>· {i.duree_min} min</span>}
                                </div>
                                <p className="mt-0.5 whitespace-pre-wrap">{i.notes}</p>
                                <div className="text-xs text-muted-foreground mt-0.5">{i.author_name} · {new Date(i.at).toLocaleString('fr-FR')}</div>
                            </li>
                        ))}
                        {interventions.length === 0 && <li className="text-sm text-foreground/60">Aucune intervention.</li>}
                    </ul>
                </div>

                <div className="card-soft p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl">Documents ({documents.length})</h2>
                        <input type="file" ref={fileRef} className="hidden" onChange={e => { uploadDoc(e.target.files[0]); e.target.value = ''; }} data-testid="adrns-doc-input" />
                        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="adrns-doc-upload-btn">
                            {uploading ? <Loader2 size={13} className="animate-spin" /> : <><Upload size={13} className="mr-1.5" /> Ajouter</>}
                        </Button>
                    </div>
                    <ul className="space-y-2 max-h-96 overflow-y-auto">
                        {documents.map(doc => (
                            <li key={doc.id} className="flex items-center gap-3 border border-border rounded-lg px-3 py-2 text-sm" data-testid={`adrns-doc-${doc.id}`}>
                                <FileText size={15} className="text-primary shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="truncate font-medium">{doc.name}</div>
                                    <div className="text-xs text-muted-foreground">{(doc.size / 1024).toFixed(0)} Ko · {new Date(doc.at).toLocaleDateString('fr-FR')}</div>
                                </div>
                                <button onClick={() => downloadDoc(doc)} className="text-muted-foreground hover:text-primary" data-testid={`adrns-doc-download-${doc.id}`}>
                                    <Download size={15} />
                                </button>
                            </li>
                        ))}
                        {documents.length === 0 && <li className="text-sm text-foreground/60">Aucun document.</li>}
                    </ul>
                </div>
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="adrns-edit-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Modifier le dossier</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Nom *" value={editForm.porteur_name || ''} onChange={e => setEditForm(f => ({ ...f, porteur_name: e.target.value }))} data-testid="adrns-edit-name" />
                        <Input placeholder="Email" value={editForm.porteur_email || ''} onChange={e => setEditForm(f => ({ ...f, porteur_email: e.target.value }))} />
                        <Input placeholder="Téléphone" value={editForm.porteur_phone || ''} onChange={e => setEditForm(f => ({ ...f, porteur_phone: e.target.value }))} />
                        <select value={editForm.org_type || 'porteur'} onChange={e => setEditForm(f => ({ ...f, org_type: e.target.value }))}
                            className="h-10 w-full px-3 rounded-md border border-input bg-background text-sm">
                            {Object.entries(ORG_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <Textarea placeholder="Besoin exprimé" rows={3} value={editForm.besoin || ''} onChange={e => setEditForm(f => ({ ...f, besoin: e.target.value }))} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
                        <Button onClick={saveEdit} data-testid="adrns-edit-save">Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
